import {
  EventsPostRequest,
  EventsEventIdPatchRequest,
  EventsEventIdDeleteRequest,
  EventsEventIdWakePostRequest,
  SmepEventPaginatedList,
  EventsEventIdAdPodsPostRequest,
} from '@nativewaves/nw-cloud-sdk/smep'
import { useQueryClient, useMutation, InfiniteData } from '@tanstack/react-query'

import { SmepAPI } from 'services/api'
import { eventQueryKeys } from 'services/queryKeys'
import { updateListCachesByCreation, updateListCachesByDeletion, updateListCachesByUpdate } from 'utils'

export const eventMutationKeys = {
  delete: () => ['smep', 'event', 'DELETE'] as const,
}

type UseEventCreateMutationProps = {
  originId?: string
}

export const useEventCreateMutation = (props: UseEventCreateMutationProps) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: EventsPostRequest) => SmepAPI.events.eventsPost(params),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: eventQueryKeys.lists(),
      })
    },
    onSettled: (data, error) => {
      if (!!data && !error) {
        queryClient.setQueriesData<InfiniteData<SmepEventPaginatedList>>(
          { queryKey: eventQueryKeys.lists() },
          updateListCachesByCreation(data, { idKey: 'id', originId: props.originId }),
        )
      }
    },
  })
}

export const useEventUpdateMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: EventsEventIdPatchRequest) => SmepAPI.events.eventsEventIdPatch(params),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: eventQueryKeys.batchGets() })
    },
    onSettled: (data, error, variables) => {
      if (!error && !!data) {
        queryClient.resetQueries({ queryKey: eventQueryKeys.batchGets() })

        queryClient.setQueriesData<InfiniteData<SmepEventPaginatedList>>(
          { queryKey: eventQueryKeys.lists() },
          updateListCachesByUpdate(data, { idKey: 'id', idToMatch: variables.eventId }),
        )

        return queryClient.invalidateQueries({ queryKey: eventQueryKeys.details() })
      }
    },
  })
}

export const useEventDeleteMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: eventMutationKeys.delete(),
    mutationFn: (params: EventsEventIdDeleteRequest) => SmepAPI.events.eventsEventIdDelete(params),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: eventQueryKeys.lists() })
      await queryClient.cancelQueries({ queryKey: eventQueryKeys.batchGets() })
    },
    onSettled: (_, error, variables) => {
      if (!error) {
        queryClient.setQueriesData<InfiniteData<SmepEventPaginatedList>>(
          { queryKey: eventQueryKeys.lists() },
          updateListCachesByDeletion({ idKey: 'id', idToMatch: variables.eventId }),
        )
      }
    },
  })
}

export const useEventWakeMutation = () => {
  return useMutation({
    mutationFn: (params: EventsEventIdWakePostRequest) => SmepAPI.events.eventsEventIdWakePost(params),
  })
}

export const useEventAdPodsCreateMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: EventsEventIdAdPodsPostRequest) => SmepAPI.events.eventsEventIdAdPodsPost(params),
    onSettled: (data, error, variables) => {
      if (!error && !!data) {
        return queryClient.invalidateQueries({
          queryKey: eventQueryKeys.adPods({ eventId: variables.eventId }).queryKey,
        })
      }
    },
  })
}
